import React, { useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import {
  NotificationService,
  NOTIFICATION_DURATIONS,
  NOTIFICATION_TYPE_ERROR,
  NOTIFICATION_TYPE_SUCCESS,
} from 'ui-components/services/notification-service';

import { addFulfillmentPlansToBatchAsync, createNewBatchAsync } from '../../../../../../store/batches/batches.api';
import { useSalesOrderIdContext } from '../../../../../context/sales-order-id-context-provider';
import { AddToBatchDropdownItems, version2 } from '../../../../../../components/add-to-batch-dropdown/add-to-batch-dropdown-items';
import {
  useEntitiesFulfillmentPlanSetFromFulfillmentPlanId,
  useAddCounterToFulfillmentPlans,
} from '../../../../../../hooks/entities/selectors/fulfillment-plans';
import Global from '../../../../../../store/global-selectors';
import { fulfillmentPlansBlockedFromBatch, addedFulfillmentPlansToBatchSuccess } from '../../../../../../store/batches/batches.actions';
import { bulkloadFulfillmentPlans } from '../../../../../../store/bulkload/bulkload.actions';
import { loadMoreActiveBatches, getBatchMenuData } from '../../../../../../store/batches/nav/batches.nav.actions';
import { stopClickPropagation } from '../../../../../../services/event-handler-helpers';
import {
  ORDER_DETAILS_ACTIONS,
  useTrackOrderDetailsAction,
} from '../../../../../modals/order-details-modal/event-tracking/use-track-order-details-action';

import messages from '../messages';
import style from './styles.module.less';

const showNotification = (message) => {
  NotificationService.showNotification({
    notificationType: message.notification,
    title: message.title && <FormattedMessage {...message.title} />,
    content: message.content && <FormattedMessage {...message.content} values={message.values} />,
    duration: NOTIFICATION_DURATIONS.SHORT,
  });
};

const createBatchMessages = (total, counter) => {
  return (batchName, isSuccess, action) => {
    const messageType = isSuccess ? 'successMessage' : 'errorMessage';
    const values = {
      shipmentCount: <FormattedNumber value={total} />,
      shipmentNumber: <FormattedNumber value={counter} />,
      batchName,
    };
    const batchMessages = {
      create: {
        successMessage: {
          title: messages.createBatchAndAddShipmentSuccessTitle,
          content: messages.addShipmentToBatchSuccessMessage,
          values,
          notification: NOTIFICATION_TYPE_SUCCESS,
        },
        errorMessage: {
          title: messages.createBatchAndAddShipmentErrorTitle,
          content: messages.createBatchAndAddShipmentErrorMessage,
          values,
          notification: NOTIFICATION_TYPE_ERROR,
        },
      },
      add: {
        successMessage: {
          title: messages.addShipmentToBatchSuccessTitle,
          content: messages.addShipmentToBatchSuccessMessage,
          values,
          notification: NOTIFICATION_TYPE_SUCCESS,
        },
        errorMessage: {
          title: messages.addShipmentToBatchErrorTitle,
          content: messages.addShipmentToBatchErrorMessage,
          values,
          notification: NOTIFICATION_TYPE_ERROR,
        },
      },
    };
    return batchMessages[action][messageType];
  };
};

const AddToBatch = memo(({ id, fulfillmentPlanId }) => {
  const salesOrderId = useSalesOrderIdContext();
  const { id: currentFulfillmentPlanSetId, name: currentFulfillmentPlanSetName } =
    useEntitiesFulfillmentPlanSetFromFulfillmentPlanId(fulfillmentPlanId);
  const { counter, total } = useAddCounterToFulfillmentPlans(salesOrderId, fulfillmentPlanId);
  const userId = useSelector(Global.selectUserCurrentUserId);
  const pageSize = useSelector(Global.selectBatchesNavPageSize);
  const totalBatchesCount = useSelector(Global.selectBatchesNavTotalBatchesCount);
  const shouldShowLoadMoreButton = pageSize < totalBatchesCount;
  const dispatch = useDispatch();
  const trackAddToBatchClick = useTrackOrderDetailsAction(ORDER_DETAILS_ACTIONS.ADD_FULFILLMENT_PLAN_TO_BATCH);
  const trackCreateBatchClick = useTrackOrderDetailsAction(ORDER_DETAILS_ACTIONS.CREATE_BATCH_ADD_FULFILLMENT_PLAN);
  const createMessage = createBatchMessages(total, counter);
  const addIdsToBatch = useCallback(
    async (fulfillmentPlanSetId, batchName, previousAction) => {
      const filter = undefined;
      const action = previousAction || 'add';
      const successMessage = createMessage(batchName, true, action);
      const errorMessage = createMessage(batchName, false, action);

      try {
        const response = await addFulfillmentPlansToBatchAsync(fulfillmentPlanSetId, [fulfillmentPlanId], filter);
        const { fulfillmentPlansInOtherBatches = [] } = response?.data || {};
        if (fulfillmentPlansInOtherBatches.length) {
          dispatch(fulfillmentPlansBlockedFromBatch(fulfillmentPlansInOtherBatches, fulfillmentPlanSetId, errorMessage, successMessage));
        } else {
          dispatch(addedFulfillmentPlansToBatchSuccess());
          dispatch(bulkloadFulfillmentPlans([fulfillmentPlanId]));
          showNotification(successMessage);
        }
      } catch {
        showNotification(errorMessage);
      }
      trackAddToBatchClick();
    },
    [createMessage, dispatch, fulfillmentPlanId, trackAddToBatchClick],
  );

  const createBatchWithIds = useCallback(async () => {
    const newBatchName = undefined;
    const undefinedNote = undefined;
    const action = 'create';
    const errorMessage = createMessage(newBatchName, false, action);
    try {
      const response = await createNewBatchAsync(newBatchName, undefinedNote, userId);
      const { fulfillmentPlanSetId, fulfillmentPlanSetName } = response?.data || {};
      dispatch(getBatchMenuData());
      await addIdsToBatch(fulfillmentPlanSetId, fulfillmentPlanSetName, action);
    } catch {
      showNotification(errorMessage);
    }
    trackCreateBatchClick();
  }, [addIdsToBatch, createMessage, dispatch, trackCreateBatchClick, userId]);

  const loadMore = useCallback(() => {
    dispatch(loadMoreActiveBatches());
  }, [dispatch]);

  return (
    <div onClick={stopClickPropagation}>
      <DropdownMenu dropdownMenuClassName={style['order-details-shipment-details-add-to-batch-dropdown']} id={id} stopScrollPropagation>
        <DropdownMenuToggler className={style['active-batch']} stopClickPropagation theme='link'>
          {currentFulfillmentPlanSetName || <FormattedMessage {...messages.addToBatch} />}
        </DropdownMenuToggler>
        <AddToBatchDropdownItems
          activeFulfillmentPlanSetId={currentFulfillmentPlanSetId}
          addIdsToBatch={addIdsToBatch}
          createBatchWithIds={createBatchWithIds}
          loadMore={loadMore}
          shouldShowLoadMoreButton={shouldShowLoadMoreButton}
          version={version2}
        />
      </DropdownMenu>
    </div>
  );
});

AddToBatch.propTypes = {
  id: PropTypes.string,
  fulfillmentPlanId: PropTypes.string.isRequired,
};

export { AddToBatch };
